import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  getExperienceList,
  getExperienceById,
  createExperience,
  updateExperience,
  patchExperience,
  deleteExperience,
} from "../api/experience.api";
import type {
  ExperiencePayload,
  GetExperienceListParams,
} from "@/interface/profile";
import { experienceKeys, candidateProfileKeys, STALE_5_MIN } from "@/lib/queryKey";

export const useExperienceList = (params?: GetExperienceListParams) =>
  useQuery({
    queryKey: experienceKeys.list(params),
    queryFn: () => getExperienceList(params),
    ...STALE_5_MIN,
  });

export const useExperienceDetail = (id: number | string, enabled = true) =>
  useQuery({
    queryKey: experienceKeys.detail(id),
    queryFn: () => getExperienceById(String(id)),
    enabled: Boolean(id) && enabled,
    ...STALE_5_MIN,
  });

export const useCreateExperience = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createExperience,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: experienceKeys.lists() });
      queryClient.invalidateQueries({ queryKey: candidateProfileKeys.experiences() });
      queryClient.invalidateQueries({ queryKey: ["profile-completion"] });
    },
  });
};

export const useUpdateExperience = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      payload,
    }: {
      id: number | string;
      payload: ExperiencePayload;
    }) => updateExperience(String(id), payload),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: experienceKeys.lists() });
      queryClient.invalidateQueries({ queryKey: experienceKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: candidateProfileKeys.experiences() });
      queryClient.invalidateQueries({ queryKey: ["profile-completion"] });
    },
  });
};

export const usePatchExperience = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      payload,
    }: {
      id: number | string;
      payload: Partial<ExperiencePayload>;
    }) => patchExperience(String(id), payload),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: experienceKeys.lists() });
      queryClient.invalidateQueries({ queryKey: experienceKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: candidateProfileKeys.experiences() });
      queryClient.invalidateQueries({ queryKey: ["profile-completion"] });
    },
  });
};

export const useDeleteExperience = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number | string) => deleteExperience(String(id)),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: experienceKeys.lists() });
      queryClient.invalidateQueries({ queryKey: candidateProfileKeys.experiences() });
      queryClient.invalidateQueries({ queryKey: ["profile-completion"] });
    },
  });
};
